'use client';

import Button from '@/components/ui/Button';
import ErrorText from '@/components/ui/ErrorText';
import Input from '@/components/ui/Input';
import Link from '@/components/ui/Link';
import { useFestivalContext } from '@/contexts/FestivalContext';
import { PathEnum } from '@/i18n/locale-config';
import { withBasePath } from '@/utils/base-path';
import { createClient } from '@/utils/supabase/client';
import { cn } from '@/utils/tailwind-merge';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale, useTranslations } from 'next-intl';
import { getGiftRedemptionQuery } from '@/utils/gift-redemption';
import { useSearchParams } from 'next/navigation';
import { isEarlyBirdOnly, ticketingAuthHref } from '@/utils/early-bird';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const resetPasswordSchema = z.object({
    email: z.string().email('invalidEmail').nonempty('Email is required'),
});

type FormData = z.infer<typeof resetPasswordSchema>;

const ForgotPasswordPage = () => {
    const t = useTranslations();
    const locale = useLocale();
    const { slug: festivalSlug, basePath, festival } = useFestivalContext();
    const searchParams = useSearchParams();
    const giftQuery = getGiftRedemptionQuery(searchParams);
    const returnToCheckout =
        (isEarlyBirdOnly(festival) || !!giftQuery) && searchParams.get('returnTo') === 'checkout';
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const supabase = createClient();

    const {
        handleSubmit,
        register,
        reset,
        formState: { errors },
    } = useForm<FormData>({
        resolver: zodResolver(resetPasswordSchema),
    });

    const onSubmit = async (formData: FormData) => {
        setSubmitting(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        // Check if user exists before attempting password reset
        try {
            const response = await fetch(
                `${withBasePath('/api/check-user-exists', basePath)}${giftQuery}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: formData.email }),
                },
            );
            const { exists } = await response.json();

            if (!exists) {
                setErrorMessage(t('PasswordReset.emailNotRegistered'));
                setSubmitting(false);
                return;
            }
        } catch (error) {
            // If check fails, proceed with reset anyway
            console.error('Error checking user existence:', error);
        }

        try {
            const redirectUrl = `${window.location.origin}/${festivalSlug}/${locale}${ticketingAuthHref('/reset-password/', returnToCheckout, giftQuery)}`;
            const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
                redirectTo: redirectUrl,
            });

            if (error) {
                setErrorMessage(t('PasswordReset.errorMessage'));
                setSubmitting(false);
                return;
            }

            setSuccessMessage(t('PasswordReset.successMessage'));
            reset();
        } catch (error) {
            console.error('Error in password reset:', error);
            setErrorMessage(t('PasswordReset.errorMessage'));
        } finally {
            setSubmitting(false);
        }
    };

    const getFieldError = (fieldName: keyof FormData) =>
        errors[fieldName]?.message && errors[fieldName]?.message !== 'Required'
            ? t(`FieldErrors.${errors[fieldName]?.message}`)
            : '';

    return (
        <form
            className={cn('flex flex-col gap-7 justify-center items-center relative text-white')}
            onSubmit={handleSubmit(onSubmit)}
            noValidate
        >
            <div className="text-center">
                <h4 className="mb-5 text-h4 lg:text-h1 uppercase text-white">
                    {t('PasswordReset.title')}
                </h4>
                <p className="font-helvetica-neue font-light text-xl">
                    {t('PasswordReset.description')}
                </p>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-3 max-w-[460px] w-full">
                <Input
                    {...register('email')}
                    className="newsletter-input"
                    type="email"
                    name="email"
                    id="email"
                    placeholder={t('Form.email')}
                    autoComplete="false"
                    color="white"
                    error={getFieldError('email')}
                    required
                />
                {errorMessage && (
                    <div className="font-trade-ghotic font-light flex flex-col">
                        <ErrorText error={errorMessage} />
                    </div>
                )}
                {successMessage && (
                    <div className="font-trade-ghotic font-light flex flex-col text-green-500">
                        {successMessage}
                    </div>
                )}
                <Button
                    className="w-full"
                    textClasses="text-white"
                    type="submit"
                    variant="secondary"
                    title={t('Form.next')}
                    submitting={submitting}
                    disabled={submitting}
                />
                <p className="mt-7 text-xl font-helvetica-neue font-light leading-6 text-center">
                    <Link
                        href={ticketingAuthHref(PathEnum.SignIn, returnToCheckout, giftQuery)}
                        className="text-primary"
                        title={t('PasswordReset.backToSignIn')}
                    />
                </p>
            </div>
        </form>
    );
};

export default ForgotPasswordPage;
